import { Injectable, inject } from '@angular/core';
import { catchError, finalize, take, throwError } from 'rxjs';
import { TaskDetailsHashmapState } from './task-details-hashmap.state';
import {
  CallPayerTaskDto,
  CreatePackageTaskDto,
  MailPackageTaskDto,
  ManualPortalTaskDto,
  TaskDto,
  TaskGroupDto,
  TaskGroupsClient,
} from '@r1-development/resolution-mechanicalturk-api-client';
import {
  ManualPortalTaskDetailsUi,
  TaskDetailsBaseUi,
  TaskDetailTypesUi,
  TaskGroupBaseUi,
} from '../../models/task-details.model';
import { HashmapStateUpdateOptions } from 'src/app/shared/state/hashmap-state-base';
import { SubmissionMethods } from 'src/app/routes/routes-with-sidebar/shared/models/submission-methods.model';
import { TaskTypes } from 'src/app/shared/models/tasks.model';
import { UrlParamsState } from '../url-params/url-params.state';

@Injectable({
  providedIn: 'root',
})
export class TaskDetailsHashmapEffect {
  private readonly taskDetailsHashmapState = inject(TaskDetailsHashmapState);
  private readonly urlParamsState = inject(UrlParamsState);
  private readonly taskGroupClient = inject(TaskGroupsClient);

  private readonly taskTypes = {
    createPackageTaskDto: 'CreatePackageTaskDto',
    mailPackageTaskDto: 'MailPackageTaskDto',
    manualPortalTaskDto: 'ManualPortalTaskDto',
  } as const;

  reloadState(
    id: string,
    options?: HashmapStateUpdateOptions,
    forceApiReload?: boolean
  ): void {
    // use taskGroupId to get the state otherwise it gets dropped on task change.
    const activeTaskGroupId = this.urlParamsState.activeTaskGroupId();
    const activeTaskId = this.urlParamsState.activeTaskId();
    // Use activeTaskGroupId if available, otherwise fallback to activeTaskId
    const activeId = activeTaskGroupId ?? activeTaskId;

    if (!activeId) {
      // No active ID to load state for
      return;
    }

    const stateManager = this.taskDetailsHashmapState.getState(activeId);
    const activeTask = stateManager.taskDetails();

    if (
      !forceApiReload &&
      options &&
      options.shouldReloadOnAging &&
      !this.taskDetailsHashmapState.stateOlderThan(
        activeId,
        options.numberOfSecondsToTreatAsStaleState
      ) &&
      id === activeTask?.id
    ) {
      // Consider in-memory state is fresh so that we don't need to reload it
      return;
    }

    stateManager.setLoading(true);

    // check if taskGroup is set
    const group = stateManager.taskGroup();

    if (stateManager.taskGroup()?.id && !forceApiReload) {
      // If taskGroup is set, we can skip the API call
      const task = group?.tasks?.find((t) => t.id === id);
      if (task) {
        stateManager.setTaskDetails(task);
        stateManager.setLoading(false);
      } else {
        // Task not found in cached group, handle missing task case
        stateManager.setLoading(false);
        stateManager.setError(new Error('Task not found in cached group'));
      }
    } else if (activeTaskGroupId) {
      // If activeTaskGroupId is set, we can use it for the API call

      this.taskGroupClient
        .getTaskGroup(activeTaskGroupId)
        .pipe(
          catchError((error) => {
            stateManager.setError(error);
            return throwError(() => {
              return error;
            });
          }),
          finalize(() => {
            stateManager.setLoading(false);
          }),
          take(1)
        )
        .subscribe((response) => {
          const groupDetails = response;
          if (groupDetails) {
            const groupUi = this.convertGroupDtoToUi(groupDetails);
            stateManager.setTaskGroup(groupUi);
            // check group.tasks for the task with the given id
            const task = groupDetails.tasks?.find((t) => t.id === id);
            if (task) {
              const taskDetailsUi: TaskDetailsBaseUi =
                this.convertTaskDetailsType(task, groupDetails);
              stateManager.setTaskDetails(taskDetailsUi);
              stateManager.setLoading(false);
            } else {
              stateManager.setLoading(false);
              stateManager.setError(
                new Error(`Task with id ${id} not found in the fetched group.`)
              );
            }
          }
        });
    }
  }

  private convertGroupDtoToUi(taskGroupDto: TaskGroupDto): TaskGroupBaseUi {
    if (!taskGroupDto?.id) {
      throw new Error('"id" field must be present');
    }
    const convertedTasks = taskGroupDto.tasks?.map((task) =>
      this.convertTaskDetailsType(task, taskGroupDto)
    );
    return {
      ...taskGroupDto,
      tasks: convertedTasks || [],
    };
  }

  // Converts from API incoming type to the type to be used in UI
  private convertTaskDetailsType(
    taskDto: TaskDto,
    group?: TaskGroupDto
  ): TaskDetailsBaseUi {
    if (!taskDto.id) {
      throw new Error('"id" field must be present');
    }

    let returnObject: TaskDetailTypesUi = {
      id: taskDto.id,
      taskType: taskDto.taskType as TaskTypes,
      status: taskDto.status,
      statusReason: taskDto.statusReason,
      canceledAt: taskDto.canceledAt as unknown as string,
      completedAt: taskDto.completedAt as unknown as string,
      completedById: taskDto.completedById,
      completedByEmail: taskDto.completedByEmail,
      completedByName: taskDto.completedByName,
      assignedAt: taskDto.assignedAt as unknown as string,
      assignedToName: group?.assignedToName,
      keyDetails: taskDto.keyDetails,
      accountId: taskDto.accountId,
      createdAt:
        (taskDto.createdAt as unknown as string) ??
        (group?.createdAt as unknown as string),
      dueDate: taskDto.dueDate,
    };
    if (this.isCreatePackageTaskDto(taskDto)) {
      returnObject = {
        ...returnObject,
        submissionMethod: taskDto.submissionMethod as SubmissionMethods,
        accountNumber: taskDto.accountNumber,
        healthSystemId: taskDto.healthSystemId,
        isPursueWorkflow: taskDto.isPursueWorkflow,
      };
    } else if (this.isMailPackageTaskDto(taskDto)) {
      returnObject = {
        ...returnObject,
        submissionMethod: taskDto.submissionMethod as SubmissionMethods,
        mailMethod: taskDto.mailMethod,
        mailingAddress: {
          address1: taskDto.mailingAddress?.address1 || '',
          address2: taskDto.mailingAddress?.address2,
          addressee: taskDto.mailingAddress?.addressee || '',
          attention: taskDto.mailingAddress?.attention || '',
          city: taskDto.mailingAddress?.city || '',
          state: taskDto.mailingAddress?.stateProvince || '',
          zipCode: taskDto.mailingAddress?.zipCode || '',
          addressType: taskDto.mailingAddress?.addressType,
        },
        returnAddress: {
          address1: taskDto.returnAddress?.address1 || '',
          address2: taskDto.returnAddress?.address2,
          addressee: taskDto.returnAddress?.addressee || '',
          attention: taskDto.returnAddress?.attention || '',
          city: taskDto.returnAddress?.city || '',
          state: taskDto.returnAddress?.stateProvince || '',
          zipCode: taskDto.returnAddress?.zipCode || '',
          addressType: taskDto.returnAddress?.addressType,
        },
        fileId: taskDto.fileId,
        fileUrl: taskDto.fileUrl,
        instructions: taskDto.instructions,
      };
    } else if (this.isManualPortalTaskDto(taskDto)) {
      returnObject = {
        ...returnObject,
        fileId: taskDto.fileId,
        payerPortalId: taskDto.payerPortalId,
        failureReason: taskDto.failureReason,
        // Cast until @r1-development/resolution-mechanicalturk-api-client is regenerated with splitFileIds.
        splitFileIds:
          (taskDto as { splitFileIds?: string[] }).splitFileIds ?? [],
      } as ManualPortalTaskDetailsUi;
    }

    return returnObject;
  }

  private isCreatePackageTaskDto(
    dto: CreatePackageTaskDto | CallPayerTaskDto | MailPackageTaskDto
  ): dto is CreatePackageTaskDto {
    return dto.$type === this.taskTypes.createPackageTaskDto;
  }

  private isMailPackageTaskDto(
    dto: CreatePackageTaskDto | CallPayerTaskDto | MailPackageTaskDto
  ): dto is MailPackageTaskDto {
    return dto.$type === this.taskTypes.mailPackageTaskDto;
  }

  private isManualPortalTaskDto(dto: TaskDto): dto is ManualPortalTaskDto {
    return dto.$type === this.taskTypes.manualPortalTaskDto;
  }
}
